// import global variables.
import {FOCUS_ELEMENTS, mql, tabindexControl} from '../utils/global';
import * as expand from '../utils/expand';
import * as backdrop from '../utils/backdrop';
import scrollLock from '../utils/scrollLock';

class Dropdown {
    /**
     * ドロップダウン
     *
     * @constructor
     * @param {HTMLElement} root - ルートとなる要素
     * @param {Object} options - 設定の変更をする際のオブジェクト
     */
    constructor(root, options) {
        const config = {
            className: {
                ctrl: 'js-dropdown__ctrl',
                content: 'js-dropdown__content',
                closeButton: 'c-button-close',
                closeButtonLabel: 'c-button-close__label'
            },
            text: {
                closeButtonLabel: '閉じる'
            },
            prefix: {
                id: 'dropdown-'
            }
        };

        if (!root) {
            return;
        }

        this.config = Object.assign(config, options);
        this.root = root;
        this.ctrls = this.root.querySelectorAll(`.${this.config.className.ctrl}`);
        this.contents = this.root.querySelectorAll(`.${this.config.className.content}`);
        this.closeButtons = [];
        this.backdrop = backdrop.createBackdropLayer();
        this.currentIndex = 0;
        this.isAnimate = false;
        this.isOpen = false;

        this.init();
        this.addEvent();
    }

    /**
     * init - 初期化
     * @return {Void}
     */
    init() {
        for (let i = 0; i < this.contents.length; i++) {
            const content = this.contents[i];

            expand.init(this.ctrls[i], content, this.config.prefix.id, false);
            content.setAttribute('role', 'group');

            // ブレイクポイント以上の画面サイズの場合
            if (mql.state === 'PC') {
                const closeButton = this.closeButtons[i];

                // 閉じるボタンが無ければ生成
                if (!closeButton) {
                    content.insertAdjacentHTML('beforeend', `<button type="button" class="${this.config.className.closeButton}"><span class="${this.config.className.closeButtonLabel}">${this.config.text.closeButtonLabel}</span></button>`);
                    this.closeButtons.push(content.lastElementChild);

                    continue;
                }

                content.insertAdjacentElement('beforeend', closeButton);
            }
        }
    }

    /**
     * getfocusableElements - フォーカス可能な要素を取得する
     * @return {Array[HTMLElement]}
     */
    getfocusableElements() {
        let focusableElements = [];

        for (const content of this.contents) {
            focusableElements = focusableElements.concat(Array.from(content.querySelectorAll(FOCUS_ELEMENTS)));
        }

        return focusableElements;
    }

    /**
     * addEvent - イベントバインド
     */
    addEvent() {
        // Escapeキー押下で閉じる
        this.root.addEventListener('keydown', (e) => {
            if (!this.isOpen || mql.state === 'SP' || e.key.indexOf('Esc') === -1) {
                return;
            }

            this.toggleClose();
        });

        for (const ctrl of this.ctrls) {
            ctrl.addEventListener('click', this.toggle.bind(this));
        }

        for (const button of this.closeButtons) {
            button.addEventListener('click', this.toggleClose.bind(this));
        }

        for (const content of this.contents) {
            content.addEventListener('transitionend', (e) => {
                // height以外のトランジションは検知しない
                if (e.propertyName !== 'height') {
                    return;
                }

                this.transitionAfter(e.target);
            });

            content.addEventListener(backdrop.eventName, this.toggleClose.bind(this));
        }

        window.addEventListener(mql.event, () => {
            // 開いた状態でブレークポイントをまたいだ場合、閉じる処理
            if (this.isOpen) {
                backdrop.displayBackdropLayer(false);
                scrollLock(false);
                tabindexControl(false, this.getfocusableElements());
            }

            // SPサイズの場合、閉じるボタンを削除
            if (mql.state === 'SP') {
                for (const closeButton of this.closeButtons) {
                    closeButton.parentNode.removeChild(closeButton);
                }
            }

            // フラグをリセット
            this.isAnimate = false;
            this.isOpen = false;

            // 初期化を実行
            this.init();
        });
    }

    /**
     * toggle - 開く または 閉じる機能を実行
     * @param  {ClickEvent} event クリックイベントオブジェクト
     * @return {Void}
     */
    toggle(event) {
        const index = [].indexOf.call(this.ctrls, event.currentTarget);

        event.preventDefault();

        // アニメーション中は何もしない
        if (this.isAnimate || index === -1) {
            return;
        }

        // PC時はドロップダウンとして動作。開いているコンテンツがある場合、すべて閉じてから開く処理を実行
        if (mql.state === 'PC' && this.isOpen) {
            this.toggleClose();

            // 開いているコンテンツを閉じるだけの場合はここで終了
            if (this.currentIndex === index) {
                return;
            }
        }

        // カレントを更新
        this.currentIndex = index;

        // SP時はトグルとして動作。コンテンツが非表示ではない場合、閉じて終了
        if (mql.state === 'SP' && !this.contents[this.currentIndex].hidden) {
            this.toggleClose();

            return;
        }

        this.toggleOpen();
    }

    /**
     * toggleOpen - トグルを開く
     * @return {Void}
     */
    toggleOpen() {
        const currentCtrl = this.ctrls[this.currentIndex];
        const currentContent = this.contents[this.currentIndex];

        // フラグを更新
        this.isOpen = true;
        this.isAnimate = true;

        // タブインデックス・フォーカスを更新
        tabindexControl(true, [...this.getfocusableElements(), currentCtrl]);
        currentCtrl.focus();

        // 開く処理を実行、false（transition-durtionが設定されていない）場合はトランジション後の処理を実行
        if (!expand.open(currentCtrl, currentContent, mql.state === 'PC')) {
            this.transitionAfter(currentContent);
        }
    }

    /**
     * toggleClose - トグルを閉じる
     * @return {Void}
     */
    toggleClose() {
        const currentCtrl = this.ctrls[this.currentIndex];
        const currentContent = this.contents[this.currentIndex];

        // フラグを更新
        this.isOpen = false;
        this.isAnimate = true;

        // タブインデックスを更新
        tabindexControl(false, this.getfocusableElements());

        // 閉じる処理を実行、false（transition-durtionが設定されていない）場合はトランジション後の処理を実行
        if (!expand.close(currentCtrl, currentContent, mql.state === 'PC')) {
            this.transitionAfter(currentContent);
        }
    }

    /**
     * transitionAfter - トランジション後の処理
     * @param  {HTMLElement} element 開閉したコンテンツ要素
     * @return {Void}
     */
    transitionAfter(element) {
        this.isAnimate = false;
        expand.transitionAfter(element);
    }
}

/**
 * デフォルト実行処理
 * @return {Void}
 */
export default () => {
    document.querySelectorAll('.js-dropdown').forEach((element) => new Dropdown(element));
};
